const CYRILLIC_REGEX = /[\u0400-\u04FF]/;

/** Minimal negative hint: avoid text in image. Kept short on purpose. */
export const FLASHCARD_IMAGE_NEGATIVE_PROMPT = "no text, no letters, no words";

/** @deprecated kept for import compatibility */
export const FLASHCARD_IMAGE_AMBIGUOUS_NEGATIVE = "";

/** @deprecated kept for import compatibility */
export const FLASHCARD_IMAGE_VISUAL_RULES = "";

/** @deprecated kept for import compatibility */
export const FLASHCARD_IMAGE_NO_TEXT_SUFFIX = "";

/** Body part words: prefix with "human" for literal body-part image */
const BODY_PARTS = new Set(
  "arm leg hand foot head ear eye nose mouth shoulder back chest stomach knee elbow neck face finger toe hair lip teeth tongue palm wrist ankle hip thumb".split(
    " "
  )
);

/** Emotion/state words: suffix with "face" for literal facial expression */
const EMOTION_WORDS = new Set(
  "happy sad angry scared tired excited nervous calm surprised bored worried proud shy jealous confused frustrated".split(
    " "
  )
);

/** Concept type for prompt routing. Kept simple and deterministic. */
export type ConceptType = "object" | "body_part" | "emotion" | "action_phrase";

/**
 * Known action phrases → short scene prompt: "person [doing X]".
 * Covers common phrasal verbs and daily actions for learning.
 */
const ACTION_PHRASES: Record<string, string> = {
  "wake up": "person waking up in bed",
  "get up": "person getting out of bed",
  "sit down": "person sitting down",
  "stand up": "person standing up",
  "lie down": "person lying down",
  "go to bed": "person going to bed",
  "go to sleep": "person sleeping in bed",
  "brush teeth": "person brushing teeth",
  "wash face": "person washing face",
  "go out": "person going out the door",
  "come in": "person coming in the door",
  "put on": "person putting on clothes",
  "take off": "person taking off clothes",
  "turn on": "person turning on a light",
  "turn off": "person turning off a light",
  "look at": "person looking at something",
  "listen to": "person listening",
  "open the door": "person opening a door",
  "close the door": "person closing a door",
  "get dressed": "person getting dressed",
  "get undressed": "person getting undressed",
  "make breakfast": "person making breakfast",
  "make bed": "person making the bed",
  "take a shower": "person taking a shower",
  "take a bath": "person taking a bath",
  "comb hair": "person combing hair",
  "dry hair": "person drying hair",
  "wake sb up": "person waking up in bed",
  "get out of bed": "person getting out of bed",
};

/** Translation snippets that suggest an action (verb) for grounding. */
const ACTION_TRANSLATION_HINTS = /\b(despertar|levantar|sentar|levantarse|acostar|dormir|cepillar|lavar|poner|quitar|encender|apagar|mirar|escuchar|abrir|cerrar|vestir|desvestir|hacer|duchar|bañar|peinar|secar|correr|caminar|saltar|comer|beber)\b/i;

function normalizeWord(value: string): string {
  return value
    .trim()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .toLowerCase();
}

function normalizePhrase(value: string): string {
  return value
    .trim()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .toLowerCase();
}

/**
 * Classifies the concept into one of: object | body_part | emotion | action_phrase.
 * Uses phrase length, known lists, and optional translation hint.
 */
export function getConceptType(
  phrase: string,
  translation?: string
): ConceptType {
  const normalized = normalizePhrase(phrase);
  const tokens = normalized.split(/\s+/).filter(Boolean);
  const firstWord = tokens[0] ?? "";

  if (tokens.length === 0) return "object";

  const translationSuggestsAction =
    translation && ACTION_TRANSLATION_HINTS.test(translation.trim());

  if (tokens.length >= 2) {
    const key = normalized;
    if (ACTION_PHRASES[key]) return "action_phrase";
    return "action_phrase";
  }

  if (tokens.length === 1) {
    if (BODY_PARTS.has(firstWord)) return "body_part";
    if (EMOTION_WORDS.has(firstWord)) return "emotion";
    if (translationSuggestsAction) return "action_phrase";
    return "object";
  }

  return "object";
}

/** Common verb → -ing for unknown action phrases. */
const VERB_GERUND: Record<string, string> = {
  put: "putting", take: "taking", make: "making", run: "running", sit: "sitting",
  get: "getting", swim: "swimming", stop: "stopping", open: "opening", close: "closing",
  drink: "drinking", eat: "eating", read: "reading", write: "writing", brush: "brushing",
  wash: "washing", look: "looking", listen: "listening", go: "going", come: "coming",
};

function toGerund(verb: string): string {
  const v = verb.toLowerCase();
  if (VERB_GERUND[v]) return VERB_GERUND[v];
  if (v.endsWith("e") && v.length > 2) return v.slice(0, -1) + "ing";
  return v + "ing";
}

/**
 * Builds prompt for action_phrase: use known map or "person [verb]ing [rest]".
 */
function toActionPrompt(phrase: string): string {
  const normalized = normalizePhrase(phrase);
  const known = ACTION_PHRASES[normalized];
  if (known) return known;

  const tokens = normalized.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return "person";
  if (tokens.length === 1) return `person ${toGerund(tokens[0])}`;
  const verb = tokens[0];
  const rest = tokens.slice(1).join(" ").trim();
  return rest ? `person ${toGerund(verb)} ${rest}` : `person ${toGerund(verb)}`;
}

/**
 * Builds a literal prompt for a single word by concept type (object / body_part / emotion).
 */
function toLiteralPrompt(word: string, conceptType: ConceptType): string {
  const w = normalizeWord(word).split(/\s+/)[0] || word.trim();
  if (!w) return word.trim();

  if (conceptType === "body_part") return `human ${w}`;
  if (conceptType === "emotion") return `${w} face`;

  return w;
}

function resolvePromptSubject(front: string, back?: string, imagePrompt?: string): string {
  if (imagePrompt?.trim()) {
    return imagePrompt.trim().replace(/\s+/g, " ");
  }
  const normalizedFront = front.trim();
  const normalizedBack = (back ?? "").trim();
  const frontHasCyrillic = CYRILLIC_REGEX.test(normalizedFront);
  const backHasCyrillic = normalizedBack ? CYRILLIC_REGEX.test(normalizedBack) : true;
  return frontHasCyrillic && normalizedBack && !backHasCyrillic ? normalizedBack : normalizedFront;
}

export interface BuildFlashcardImagePromptOptions {
  mode?: "initial" | "regenerate";
  deckTitle?: string;
}

/**
 * Builds a simple literal image prompt by concept type.
 * - object: word as-is
 * - body_part: "human X"
 * - emotion: "X face"
 * - action_phrase: "person [doing X]"
 */
export function buildFlashcardImagePrompt(
  front: string,
  back?: string,
  imagePrompt?: string,
  options?: BuildFlashcardImagePromptOptions
): string {
  const subject = resolvePromptSubject(front, back, imagePrompt);
  if (!subject) return "";

  if (imagePrompt?.trim()) {
    const literal = subject.replace(/\s+/g, " ").trim();
    return options?.mode === "regenerate" ? `${literal}, different angle` : literal;
  }

  const translation = back?.trim();
  const conceptType = getConceptType(subject, translation);

  let prompt: string;
  if (conceptType === "action_phrase") {
    prompt = toActionPrompt(subject);
  } else {
    prompt = toLiteralPrompt(subject, conceptType);
  }

  if (options?.mode === "regenerate") {
    return `${prompt}, different angle`;
  }
  return prompt;
}

export function buildSmartImagePrompt(word: string, context?: string): string {
  return buildFlashcardImagePrompt(word, context, undefined);
}

/**
 * Builds a short, literal prompt for AI image generation.
 * Focus: one clear subject, simple, educational, flashcard-friendly.
 */
export function buildVisualPromptForAi(
  front: string,
  back?: string,
  imagePrompt?: string
): string {
  const base = buildFlashcardImagePrompt(front, back, imagePrompt);
  if (!base.trim()) return "";

  const subject = base.trim().replace(/\s*,\s*different angle\s*$/i, "");
  const translation = back?.trim();
  const conceptType = getConceptType(subject, translation);

  if (conceptType === "action_phrase") {
    return `single clear scene: ${subject}, simple educational style, plain background`;
  }
  if (conceptType === "body_part") {
    return `single ${subject}, isolated on plain background, educational flashcard style`;
  }
  if (conceptType === "emotion") {
    return `single ${subject}, simple expression, plain background`;
  }

  return `single ${subject} on plain background, educational flashcard style, no text`;
}

/**
 * Ensures prompt has minimal no-text hint when passed to image API.
 */
export function ensureFlashcardImagePromptConstraints(
  prompt: string,
  _options?: { mode?: "initial" | "regenerate" }
): string {
  const normalized = prompt.trim();
  if (!normalized) return normalized;

  const lower = normalized.toLowerCase();
  const hasNoText = lower.includes("no text") || lower.includes("no words");
  if (hasNoText) return normalized;

  return `${normalized}. No text in image.`;
}

// --- Legacy exports ---

export interface DisambiguationContext {
  translation?: string;
  deckTitle?: string;
}

export interface DisambiguationResult {
  literalConcept: string;
  excludePhrases: string[];
  isBodyPart: boolean;
  isAmbiguous: boolean;
}

export function disambiguateConcept(
  word: string,
  context?: DisambiguationContext
): DisambiguationResult {
  const subject = normalizePhrase(word).split(/\s+/)[0] || word.trim();
  const conceptType = getConceptType(word, context?.translation);
  const isBody = conceptType === "body_part";
  const literal =
    conceptType === "action_phrase"
      ? toActionPrompt(word)
      : toLiteralPrompt(word, conceptType);
  return {
    literalConcept: literal,
    excludePhrases: [],
    isBodyPart: isBody,
    isAmbiguous: false,
  };
}

export interface ExpandWordOptions {
  translation?: string;
  deckTitle?: string;
}

export function expandWordToVisualScene(
  word: string,
  _context?: string,
  options?: ExpandWordOptions
): string {
  const conceptType = getConceptType(word, options?.translation);
  if (conceptType === "action_phrase") return toActionPrompt(word);
  return toLiteralPrompt(word, conceptType);
}
